import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import Typography from '@nubank/nuds-web/components/Typography/Typography';
import dynamic from 'next/dynamic';

import ShortForm from 'patterns/ShortForm/ShortForm';
import ProductPageOpenFormSection from 'patterns/ProductPageOpenFormSection/ProductPageOpenFormSection';
import RegistrationUnavailableLanguage from 'patterns/RegistrationUnavailableLanguage/RegistrationUnavailableLanguage';
import useOpenFormVisibility from 'utils/hooks/useOpenFormVisibility/useOpenFormVisibility';
import { DEFAULT_LOCALE } from 'utils/i18nUtils';
import { CREDIT_CARD } from 'utils/prospectTypes';
import Experiment from '@nubank/www-latam-commons/components/Experiment/Experiment';
import { PROGRESSIVE_DISCLOSURE_FORM_V2 } from 'utils/experiments';

const ProgressiveDisclosureForm = dynamic(import('patterns/ProgressiveDisclosureForm/ProgressiveDisclosureForm'), { ssr: false });

function SectionForm({ onFormVisibilityChange }) {
  const { locale } = useIntl();
  const [ref, isFormHidden] = useOpenFormVisibility();

  useEffect(() => {
    onFormVisibilityChange(isFormHidden);
  }, [isFormHidden]);

  return (
    <ProductPageOpenFormSection ref={ref}>
      <ProductPageOpenFormSection.Container
        gridTemplateRows={{ xs: 'auto 16rem 6rem 1fr', md: 'auto 1fr', lg: '1fr' }}
      >
        <ProductPageOpenFormSection.Heading>
          <Typography
            variant="subtitle"
            tag="h1"
            color="black"
            intlKey="REWARDS.HERO.TITLE"
            gutterBottom={1.5}
          />
          <Typography
            variant="title"
            tag="h2"
            color="black"
            intlKey="REWARDS.HERO.SUBTITLE"
            intlValues={{ br: '' }}
          />
        </ProductPageOpenFormSection.Heading>
        <ProductPageOpenFormSection.ImageBlock
          gridColumn={{ xs: '1 / span 4', md: '2 / span 4', lg: '5 / span 4' }}
          srcSet={{
            xs: {
              '1x': 'rewards-app/rewards-app-xs@1x.png',
              '2x': 'rewards-app/rewards-app-xs@2x.png',
              '3x': 'rewards-app/rewards-app-xs@3x.png',
            },
            md: {
              '1x': 'rewards-app/rewards-app-md@1x.png',
              '2x': 'rewards-app/rewards-app-md@2x.png',
              '3x': 'rewards-app/rewards-app-md@3x.png',
            },
            lg: {
              '1x': 'rewards-app/rewards-app-lg@1x.png',
              '2x': 'rewards-app/rewards-app-lg@2x.png',
              '3x': 'rewards-app/rewards-app-lg@3x.png',
            },
          }}
        />
        <ProductPageOpenFormSection.FormBlock>
          {locale === DEFAULT_LOCALE ? (
            <Experiment id={PROGRESSIVE_DISCLOSURE_FORM_V2.id}>
              <Experiment.Original>
                <ShortForm
                  prospectTypeKey={CREDIT_CARD}
                  formTitle="SHORT_FORM.CREDIT_CARD.FORM_TITLE"
                  completeFormTitle="SHORT_FORM.CREDIT_CARD.COMPLETE_FORM_TITLE"
                  completeFormVerticalTitle="SHORT_FORM.CREDIT_CARD.COMPLETE_FORM_VERTICAL_TITLE"
                />
              </Experiment.Original>
              <Experiment.Variant id={PROGRESSIVE_DISCLOSURE_FORM_V2.emailsFieldsForm}>
                <ProgressiveDisclosureForm
                  prospectTypeKey={CREDIT_CARD}
                  formTitle="SHORT_FORM.CREDIT_CARD.FORM_TITLE"
                  completeFormName="completeFormB"
                />
              </Experiment.Variant>
              <Experiment.Variant
                id={PROGRESSIVE_DISCLOSURE_FORM_V2.emailFieldConfirmationStepForm}
              >
                <ProgressiveDisclosureForm
                  prospectTypeKey={CREDIT_CARD}
                  formTitle="SHORT_FORM.CREDIT_CARD.FORM_TITLE"
                  completeFormName="completeFormC"
                />
              </Experiment.Variant>
            </Experiment>
          ) : (
            <RegistrationUnavailableLanguage isOpenForm isShortForm />
          )}
        </ProductPageOpenFormSection.FormBlock>
      </ProductPageOpenFormSection.Container>
    </ProductPageOpenFormSection>
  );
}

SectionForm.propTypes = {
  onFormVisibilityChange: PropTypes.func.isRequired,
};

export default React.memo(SectionForm);
